// src/features/task/services/taskCRUD.ts
// 單一 task 的增刪改：createTask, updateTask, deleteTask,
// updateTaskStatus, incrementMigrationCount, generateMockData
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  addDoc,
  UpdateData,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import {
  Task,
  TaskStatus,
  TaskCategory,
  TaskPriority,
  TaskEnergyLevel,
  TaskCloseReason,
  TaskUpdateData,
} from '../../../types/task';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { logger } from '../../../utils/logger';
import {
  TASKS_COLLECTION,
  FirestoreUpdateData,
  convertFirestoreTaskToTask,
  prepareTaskForFirestore,
} from './taskConverters';
import { createTaskLocal } from '../../../services/indexedDB';

export type { TaskUpdateData };

export const createTask = async (taskData: {
  title: string;
  category?: TaskCategory;
  targetDate?: string;
  notes?: string;
  priority?: TaskPriority;
  energyLevel?: TaskEnergyLevel;
  estimatedTime?: number;
  userId: string;
  relatedTaskID?: string;
  relatedTaskTitle?: string;
  tags?: string[];
  location?: string;
  isQuickTask?: boolean;
  originCaptureId?: string;
  objectiveId?: string;
}): Promise<Task> => {
  try {
    const newTaskData: Record<string, unknown> = {
      title: taskData.title,
      status: 'OPEN' as TaskStatus,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      migrationCount: 0,
      userId: taskData.userId,
      targetDate: taskData.targetDate || '',
    };

    if (taskData.category !== undefined) newTaskData.category = taskData.category;
    if (taskData.notes !== undefined) newTaskData.notes = taskData.notes;
    if (taskData.priority !== undefined) newTaskData.priority = taskData.priority;
    if (taskData.energyLevel !== undefined) newTaskData.energyLevel = taskData.energyLevel;
    if (taskData.estimatedTime !== undefined) newTaskData.estimatedTime = taskData.estimatedTime;
    if (taskData.relatedTaskID !== undefined) newTaskData.relatedTaskID = taskData.relatedTaskID;
    if (taskData.relatedTaskTitle !== undefined) newTaskData.relatedTaskTitle = taskData.relatedTaskTitle;
    if (taskData.tags !== undefined) newTaskData.tags = taskData.tags;
    if (taskData.location !== undefined) newTaskData.location = taskData.location;
    if (taskData.isQuickTask !== undefined) newTaskData.isQuickTask = taskData.isQuickTask;
    if (taskData.originCaptureId !== undefined) newTaskData.originCaptureId = taskData.originCaptureId;
    if (taskData.objectiveId !== undefined) newTaskData.objectiveId = taskData.objectiveId;

    const docRef = await addDoc(collection(db, TASKS_COLLECTION), newTaskData);
    const taskDoc = await getDoc(docRef);

    if (!taskDoc.exists()) {
      throw new Error('創建任務後無法獲取任務數據');
    }

    const createdTask = convertFirestoreTaskToTask(taskDoc);

    try {
      await createTaskLocal(createdTask);
    } catch (err) {
      logger.warn('createTask', 'Failed to cache task in IndexedDB', err);
    }

    eventBus.publish(EVENTS.TASK.CREATED, createdTask.id, createdTask);

    return createdTask;
  } catch (error) {
    logger.error('創建任務失敗:', error);
    throw error;
  }
};

export const updateTask = async (taskId: string, updateData: TaskUpdateData, userId: string): Promise<Task> => {
  try {
    const taskDoc = await getDoc(doc(db, TASKS_COLLECTION, taskId));

    if (!taskDoc.exists()) {
      throw new Error('任務不存在');
    }

    const existingTask = convertFirestoreTaskToTask(taskDoc);

    if (existingTask.userId !== userId) {
      throw new Error('無權限修改此任務');
    }

    const cleanedData = Object.entries(updateData).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value === null ? null : value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    cleanedData.updatedAt = serverTimestamp();

    const taskToUpdate: FirestoreUpdateData = prepareTaskForFirestore(cleanedData);

    await updateDoc(doc(db, TASKS_COLLECTION, taskId), taskToUpdate as UpdateData<Task>);

    const updatedTask = await fetchTaskAfterUpdate(taskId);
    eventBus.publish(EVENTS.TASK.UPDATED, taskId, updatedTask);

    return updatedTask;
  } catch (error) {
    logger.error('更新任務失敗:', error);
    throw error;
  }
};

export const deleteTask = async (taskId: string, userId: string): Promise<void> => {
  try {
    const taskDoc = await getDoc(doc(db, TASKS_COLLECTION, taskId));

    if (!taskDoc.exists()) {
      throw new Error('任務不存在');
    }

    const existingTask = convertFirestoreTaskToTask(taskDoc);

    if (existingTask.userId !== userId) {
      throw new Error('無權限刪除此任務');
    }

    await deleteDoc(doc(db, TASKS_COLLECTION, taskId));
    eventBus.publish(EVENTS.TASK.DELETED, taskId, existingTask.userId);
  } catch (error) {
    logger.error('刪除任務失敗:', error);
    throw error;
  }
};

export const updateTaskStatus = async (
  taskId: string,
  newStatus: TaskStatus,
  userId: string,
  closeReason?: TaskCloseReason
): Promise<Task> => {
  try {
    const taskDoc = await getDoc(doc(db, TASKS_COLLECTION, taskId));

    if (!taskDoc.exists()) {
      throw new Error('任務不存在');
    }

    const existingTask = convertFirestoreTaskToTask(taskDoc);

    if (existingTask.userId !== userId) {
      throw new Error('無權限修改此任務');
    }

    const updateData: Record<string, unknown> = {
      status: newStatus,
      updatedAt: serverTimestamp(),
    };

    if (newStatus === 'CLOSED') {
      updateData.closedAt = serverTimestamp();
      if (closeReason) {
        updateData.closeReason = closeReason;
      }
    } else if (newStatus === 'OPEN' && existingTask.closedAt) {
      updateData.closedAt = null;
      updateData.closeReason = null;
    }

    await updateDoc(doc(db, TASKS_COLLECTION, taskId), updateData as UpdateData<Task>);

    const updatedTask = await fetchTaskAfterUpdate(taskId);
    eventBus.publish(EVENTS.TASK.STATUS_CHANGED, taskId, newStatus, updatedTask);

    return updatedTask;
  } catch (error) {
    logger.error('更新任務狀態失敗:', error);
    throw error;
  }
};

export const incrementMigrationCount = async (taskId: string, userId: string): Promise<Task> => {
  try {
    const taskDoc = await getDoc(doc(db, TASKS_COLLECTION, taskId));

    if (!taskDoc.exists()) {
      throw new Error('任務不存在');
    }

    const existingTask = convertFirestoreTaskToTask(taskDoc);

    if (existingTask.userId !== userId) {
      throw new Error('無權限修改此任務');
    }

    const newMigrationCount = (existingTask.migrationCount || 0) + 1;

    await updateDoc(doc(db, TASKS_COLLECTION, taskId), {
      migrationCount: newMigrationCount,
      updatedAt: serverTimestamp(),
    });

    const updatedTask = await fetchTaskAfterUpdate(taskId);
    eventBus.publish(EVENTS.TASK.MIGRATION_INCREMENTED, taskId, newMigrationCount, updatedTask);

    return updatedTask;
  } catch (error) {
    logger.error('增加任務遷移計數失敗:', error);
    throw error;
  }
};

export const generateMockData = async (userId: string): Promise<void> => {
  try {
    const existingQuery = query(
      collection(db, TASKS_COLLECTION),
      where('userId', '==', userId),
      where('status', '==', 'OPEN')
    );
    const existingSnapshot = await getDocs(existingQuery);

    if (!existingSnapshot.empty) {
      return;
    }

    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    nextWeek.setHours(17, 0, 0, 0);

    const mockTasks = [
      {
        title: 'Write Product Proposal',
        status: 'OPEN' as TaskStatus,
        targetDate: nextWeek.toISOString(),
        category: 'work' as TaskCategory,
        priority: '1' as TaskPriority,
        estimatedTime: 120,
        migrationCount: 0,
        notes: 'Q1 Product Planning',
        relatedTaskTitle: 'Q1 Product Planning',
        userId,
      },
    ];

    const tasksRef = collection(db, TASKS_COLLECTION);
    const createdTaskIds: string[] = [];

    for (const task of mockTasks) {
      try {
        const newTaskRef = doc(tasksRef);
        const taskData = {
          title: task.title,
          status: task.status,
          targetDate: task.targetDate || '',
          category: task.category || null,
          priority: task.priority || null,
          estimatedTime: task.estimatedTime || null,
          migrationCount: task.migrationCount || 0,
          notes: task.notes || null,
          relatedTaskTitle: task.relatedTaskTitle || null,
          userId: task.userId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          closedAt: null,
        };

        await setDoc(newTaskRef, taskData);
        createdTaskIds.push(newTaskRef.id);
      } catch (err) {
        // 個別 mock task 失敗不中斷整體流程
        logger.error('生成單筆 mock task 失敗:', err);
      }
    }

    if (createdTaskIds.length > 0) {
      eventBus.publish(EVENTS.TASK.BATCH_CREATED, createdTaskIds, userId);
    }
  } catch (error) {
    logger.error('生成測試數據失敗:', error);
    throw error;
  }
};

const fetchTaskAfterUpdate = async (taskId: string): Promise<Task> => {
  const updatedTaskDoc = await getDoc(doc(db, TASKS_COLLECTION, taskId));
  if (!updatedTaskDoc.exists()) {
    throw new Error('Task not found after update');
  }
  return convertFirestoreTaskToTask(updatedTaskDoc);
};

export const taskCRUDMethods = {
  createTask,
  updateTask,
  deleteTask,
  updateTaskStatus,
  incrementMigrationCount,
  generateMockData,
};
